import type { FC, ReactNode } from 'react'
import type { DeepSkyObject } from '@/constants/deep-sky'

import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FadeLink } from '@/components/generals/fade-link'
import { Reveal, RevealLines } from '@/components/generals/reveal'
import { ArcDivider } from '@/components/generals/hairline'
import { ObjectImage } from '@/components/catalogue/object-image'
import { COMPASS_WORDS, compassPoint, eqjToHorizon, horizonToAltAz, raDecToVector } from '@/lib/astro/sky'
import { dateKey, formatTime, fromLocal, nightKeyFor, parseDateKey } from '@/lib/time'
import { DEEP_SKY_OBJECTS } from '@/constants/deep-sky'

const visibilityTonight = (object: DeepSkyObject): string => {
    const key = nightKeyFor(new Date())
    const { year, month, day } = parseDateKey(key)
    const vector = raDecToVector(object.ra * 15, object.dec)
    let best = { alt: -90, az: 0, time: new Date() }
    let atTen = { alt: -90, az: 0 }

    for (let hour = 20; hour <= 29; hour++) {
        const time = fromLocal(year, month, day, hour, 0)
        const m = eqjToHorizon(time)
        const hx = m[0] * vector[0] + m[1] * vector[1] + m[2] * vector[2]
        const hy = m[3] * vector[0] + m[4] * vector[1] + m[5] * vector[2]
        const hz = m[6] * vector[0] + m[7] * vector[1] + m[8] * vector[2]
        const position = horizonToAltAz(hx, hy, hz)

        if (hour === 22) atTen = position
        if (position.alt > best.alt) best = { ...position, time }
    }

    if (atTen.alt > 45) return `High in the ${COMPASS_WORDS[compassPoint(atTen.az)]} at ten tonight, ${Math.round(atTen.alt)}° up.`
    if (atTen.alt > 20) return `Well placed in the ${COMPASS_WORDS[compassPoint(atTen.az)]} at ten tonight, ${Math.round(atTen.alt)}° up.`
    if (best.alt > 20) return `Best later tonight: ${Math.round(best.alt)}° up in the ${COMPASS_WORDS[compassPoint(best.az)]} around ${formatTime(best.time)}.`
    if (best.alt > 5) return `Low tonight, reaching only ${Math.round(best.alt)}° in the ${COMPASS_WORDS[compassPoint(best.az)]}.`

    return 'Not above the horizon in darkness tonight.'
}

const Catalogue: FC = (): ReactNode => {
    const [visibility, setVisibility] = useState<Record<string, string>>({})
    const [found, setFound] = useState<string[]>([])
    const [day, setDay] = useState('')

    useEffect(() => {
        const result: Record<string, string> = {}

        for (const object of DEEP_SKY_OBJECTS) result[object.id] = visibilityTonight(object)

        setVisibility(result)
        setDay(dateKey(new Date()))

        try {
            const saved = JSON.parse(localStorage.getItem('aphelion:eyepiece-found') ?? '[]') as unknown

            if (Array.isArray(saved)) setFound(saved.filter((id): id is string => typeof id === 'string'))
        }
        catch {
            // Nothing found yet.
        }
    }, [])

    return (
        <Layout>
            <Seo
                title='Deep sky catalogue'
                description='Twelve of the finest deep-sky objects for a small telescope, with how to find each one, the best season, and whether it is up tonight from Exmoor.'
                ogImage='/images/sky/m51-whirlpool.jpg'
            />

            <section className='container-page pb-16 pt-32 md:pt-40'>
                <p className='label-mono text-brass'>
                    Catalogue
                </p>
                <RevealLines as='h1' lines={['Twelve things', 'hiding in the dark']} className='display-serif mt-5 text-6xl text-star md:text-8xl' />
                <Reveal variant='wipe' className='mt-8 max-w-2xl'>
                    <p className='text-lg text-star-2'>
                        The objects from the eyepiece on our home page, with everything you need to find them yourself. Visibility is worked out for our roof, tonight.
                    </p>
                </Reveal>
                <p className='label-mono mt-8 text-star-3'>
                    {`${found.length} of ${DEEP_SKY_OBJECTS.length} found in the eyepiece`}
                    <FadeLink href='/#eyepiece' className='ml-4 text-star underline decoration-brass/60 underline-offset-4'>
                        Go hunting
                    </FadeLink>
                </p>
            </section>

            <nav aria-label='Objects' className='container-page pb-12'>
                <ul className='flex flex-wrap gap-2'>
                    {DEEP_SKY_OBJECTS.map(object => (
                        <li key={object.id}>
                            <a href={`#${object.slug}`} className='label-mono inline-flex border border-hair px-3 py-1.5 text-[0.62rem] text-star-2 transition-colors hover:border-brass hover:text-star'>
                                {object.designation.split(' · ')[0]}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='container-page'>
                <ArcDivider />
            </div>

            <section aria-label='Catalogue entries' className='container-page pb-32'>
                <ol>
                    {DEEP_SKY_OBJECTS.map((object, index) => (
                        <li key={object.id} id={object.slug} className='scroll-mt-24 border-b border-hair py-16'>
                            <article className='grid gap-10 lg:grid-cols-12 lg:gap-16'>
                                <Reveal className='lg:col-span-5'>
                                    <ObjectImage object={object} />
                                    <p className='label-mono mt-3 text-[0.58rem] normal-case tracking-[0.04em] text-star-3'>
                                        {object.credit}
                                    </p>
                                </Reveal>
                                <div className='lg:col-span-7'>
                                    <p className='label-mono flex flex-wrap items-center gap-3 text-brass'>
                                        <span>
                                            {`${String(index + 1).padStart(2, '0')} · ${object.designation}`}
                                        </span>
                                        {found.includes(object.id) && (
                                            <span className='inline-flex items-center gap-1.5 border border-brass/50 px-2 py-0.5 text-[0.58rem]'>
                                                <Check aria-hidden className='size-3' strokeWidth={1.5} />
                                                Found
                                            </span>
                                        )}
                                    </p>
                                    <h2 className='mt-3 font-heading text-5xl font-light text-star md:text-6xl'>
                                        {object.name}
                                    </h2>
                                    <p className='mt-5 font-heading text-2xl font-light leading-snug text-star-2'>
                                        {object.sentence}
                                    </p>
                                    <p className='mt-5 text-star-2'>
                                        {object.description}
                                    </p>

                                    <dl className='mt-8 grid gap-px border border-hair bg-hair sm:grid-cols-2'>
                                        {[
                                            ['Type', object.type],
                                            ['Constellation', object.constellation],
                                            ['Distance', object.distance],
                                            ['Brightness', `Magnitude ${object.magnitude}`],
                                            ['Best season', object.season],
                                            ['On our deck', object.instrument]
                                        ].map(([term, value]) => (
                                            <div key={term} className='bg-ink p-4'>
                                                <dt className='label-mono text-star-3'>
                                                    {term}
                                                </dt>
                                                <dd className='mt-1 text-star'>
                                                    {value}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>

                                    <div className='mt-8'>
                                        <p className='label-mono text-star-3'>
                                            How to find it
                                        </p>
                                        <p className='mt-2 text-star-2'>
                                            {object.finding}
                                        </p>
                                    </div>

                                    <p className='mt-8 flex items-start gap-3 border-l border-brass pl-4 text-star' aria-live='polite'>
                                        <span className='label-mono mt-1 shrink-0 text-brass'>
                                            {day ? 'Tonight' : 'Working out tonight'}
                                        </span>
                                        <span>
                                            {visibility[object.id] ?? ''}
                                        </span>
                                    </p>
                                </div>
                            </article>
                        </li>
                    ))}
                </ol>
            </section>
        </Layout>
    )
}

export default Catalogue
